<template>
  <v-card height="700px">
    <v-card-title>
      <v-toolbar color="transparent" flat>
        <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-tabs v-model="tab" right style="max-width: 450px">
          <v-tab>
            <v-badge :value="emailErrors.length" color="pink" dot>
              {{ $t('Users.Form.general') }}
            </v-badge>
          </v-tab>
          
          <v-tab>
            <v-badge :value="accessValueErrors.length" color="pink" dot>
              {{ $t('Users.Form.access') }}
            </v-badge>
          </v-tab>
          <v-tab>
            {{ $t('Users.Form.contacts') }}
          </v-tab>
        </v-tabs>
      </v-toolbar>
    </v-card-title>

    <v-card-text style="height:79%">
      <v-container>
        
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <v-text-field 
                  v-model="user.email" 
                  :label="$t('Users.Form.email')" 
                  :error-messages="emailErrors"
                  required
                  prepend-inner-icon="email"
                  @input="$v.user.email.$touch()"
                  @blur="$v.user.email.$touch()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field v-model="user.firstName" :label="$t('Users.Form.firstname')"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <v-text-field v-model="user.lastName" :label="$t('Users.Form.lastname')"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field v-model="user.phone" prepend-inner-icon="phone" :label="$t('Users.Form.phone')"></v-text-field>
              </v-col>
              
              <v-col cols="12" sm="6" md="4">
                <UserRoleSelector v-model="user.roles" :disabled="user.id === currentUser.id"/>
              </v-col>
              <v-col cols="12" sm="6" md="8">
                <GroupSelector :groups.sync="user.groups" outline v-if="!isAdminForm"/>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item>
            <v-row>
              <v-col cols="6">
                <v-select 
                  :items="accessTypes" 
                  v-model="user.accessType" 
                  label="Access" 
                  @input="user.accessValue = null" 
                  prepend-inner-icon="corporate_fare"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="6">
                <QlikCustomSelector 
                  v-model="user.accessValue"
                  v-if="user.accessType && user.accessType !== 'all'" 
                  :label="$t('Global.Common.' + user.accessType)"
                  :qDimensionId="accessTypes.find(el => el.value === user.accessType).qDimensionId"
                  :qAppId="mainQlikAppId"
                  :error="accessValueErrors.length > 0"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field 
                  v-model="user.agency" 
                  :label="$t('Global.Common.agency')" 
                  prepend-inner-icon="account_tree"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item>
            <v-row>
              <v-col cols="12">
                <UserSelector 
                  v-model="user.contactCommercial" 
                  :users="usersList.filter(user => user.email.match(RegExp('sogec', 'i')))" 
                  :label="$t('Contact.commercial')"
                />
              </v-col>
              <v-col cols="12">
                <UserSelector 
                  v-model="user.contactOperational" 
                  :users="usersList.filter(user => user.email.match(RegExp('sogec', 'i')))" 
                  :label="$t('Contact.operational')"
                />
              </v-col>
              <v-col cols="12">
                <UserSelector 
                  v-model="user.contactAccounting" 
                  :users="usersList.filter(user => user.email.match(RegExp('sogec', 'i')))" 
                  :label="$t('Contact.accounting')"
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-container>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="grey" text @click="cancel">{{$t('Global.Action.cancel')}}</v-btn>
      <v-btn color="primary" text :loading="loading" @click="save">{{$t('Global.Action.save')}}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { required, requiredIf, email } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import { isAdmin } from '@/config/roles'
import { ACCESS_TYPES } from '@/config/access'
import GroupSelector from '@/components/Utils/GroupSelector'
import UserRoleSelector from '@/components/Utils/UserRoleSelector'
import UserSelector from '@/components/Utils/UserSelector'
import QlikCustomSelector from '@/components/Qlik/QlikCustomSelector'
export default {
  name: 'UserEditor',
  components: { 
    GroupSelector, 
    UserRoleSelector, 
    UserSelector,
    QlikCustomSelector
  },
  props: {
    defaultUser: {type: Object, required: false},
    defaultFormTitle: {type: String, required: false},
    loading: {type: Boolean, required: false}
  },
  data: () => ({
    tab: null,
    search: '',
    user: null,
    default: {
      email: '',
      firstName: '',
      lastName: '',
      qlikId: '',
      qlikDirectory: '',
      phone: '',
      agency: '',
      roles: [],
      groups: [],
      accessType: null,
      accessValue: '',
    },
    groups: []
  }),
  computed: {
    isAdminForm () {
      return isAdmin(this.user)
    },
    mainQlikAppId () {
      return process.env.VUE_APP_QLIK_MAIN_APP_ID
    },
    accessTypes () {
      return ACCESS_TYPES.map(type => ({
        ...type,
        text: this.$t('Global.Common.' + type.value)
      }))
    },
    ...mapState('auth', {
      currentUser: 'user'
    }),
    ...mapState('user', [
      'usersList'
    ]),
    formTitle () {
      return this.defaultFormTitle || (this.user.id ? 'Modification' : 'Création')
    },
    emailErrors () {
      const errors = []
      if (!this.$v.user.email.$dirty) return errors
      !this.$v.user.email.isUnique && errors.push(this.$t('Global.Common.alreadyInUse'))
      !this.$v.user.email.email && errors.push(this.$t('Global.Common.invalid'))
      !this.$v.user.email.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    accessValueErrors () {
      const errors = []
      if (!this.$v.user.accessValue.$dirty || !this.user.accessType || this.user.accessType === 'all') return errors

      !this.$v.user.accessValue.required && errors.push(this.$t('Global.Common.required'))
      
      return errors
    },
  },
  watch: {
    defaultUser () {
      this.resetForm()
    }
  },
  validations: {
    user: {
      email: { required, email, isUnique(value) {
        // standalone validator ideally should not assume a field is required
        if (value === '') return true

        return !this.usersList.some(user => user.email.toLowerCase() === value.toLowerCase() && user.id !== this.user.id)
      }},
      accessValue: { required: requiredIf(user => user.accessType && user.accessType !== 'all')},
    }
  },
  methods: {
    init () {
      this.user = this.defaultUser ? {...this.defaultUser} : {...this.default}
    },
    save () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('save', this.user)
      } else {
        this.$emit('invalid-form')
      }
    },
    cancel () {
      this.$emit('cancel')
      this.resetForm()
    },
    resetForm () {
      this.init()
      this.$v.$reset()
    }
  },
  async created () {
    this.init()
  }
}
</script>